import { useState } from "react";
import Icon from "../Icon";
import { parseTimeInput, fmtHours } from "../../utils";
import {
  add, quitar, toggle, setTexto, setMinutos, mover, descuadre, normaliza,
} from "../../subtareas";
import Campo from "./Campo";

// El desglose opcional de una entrada.
//
// Controlado del todo: el unico estado propio es el campo de "nueva subtarea".
// La lista vive en la entrada y `updateEntry` la reemplaza de forma sincrona
// con {...a, ...patch}, asi que no hace falta copia local y no se pierden
// teclas al escribir.
//
// onChange recibe la lista ENTERA ya recalculada y una bandera que dice si el
// cambio viene de teclear: marcar, borrar, reordenar y anadir se guardan al
// instante; teclear pasa por el debounce de 600 ms que ya existe.

const fmtDur = min => {
  const v = Math.max(0, Math.round(min || 0));
  const h = Math.floor(v / 60), m = v % 60;
  if (!h) return m + "m";
  return m ? h + "h " + m + "m" : h + "h";
};

// Acepta "30", "30m", "1h", "1.5h" — lo mismo que el campo de tiempo de la
// entrada, para no tener dos gramaticas distintas en el mismo formulario.
function MinutosInput({ value, onChange }) {
  const [raw, setRaw] = useState(null);
  const cerrar = () => {
    if (raw === null) return;
    const t = raw.trim();
    onChange(t === "" ? null : Math.round(parseTimeInput(t) * 60));
    setRaw(null);
  };
  return (
    <input
      value={raw !== null ? raw : (value ? fmtHours(value / 60) : "")}
      onFocus={() => setRaw(value ? String(value) : "")}
      onChange={e => setRaw(e.target.value)}
      onBlur={cerrar}
      onKeyDown={e => { if (e.key === "Enter") { e.preventDefault(); e.target.blur(); } }}
      placeholder="—"
      title="Duración de la subtarea: 30, 30m, 1h, 1.5h"
      style={{ width: 58, flexShrink: 0, padding: "5px 6px", borderRadius: 7, textAlign: "center",
        border: "1px solid var(--border)", fontSize: 11.5, fontFamily: "var(--font)",
        background: "var(--surface)", color: value ? "var(--text)" : "var(--text3)" }}/>
  );
}

export default function SubtareasEditor({
  items = [], minutosEntrada = 0, onChange, onAjustarTiempo,
}) {
  const lista = normaliza(items);
  const [nueva, setNueva] = useState("");
  const [abierto, setAbierto] = useState(lista.length > 0);

  const emitir = (next, texto = false) => onChange?.(next, { texto });

  const anadir = () => {
    const t = nueva.trim();
    if (!t) return;
    emitir(add(lista, t));
    setNueva("");
  };

  // Sin subtareas, un enlace y nada mas: quien no las use no tiene por qué
  // cargar con un bloque vacío en cada entrada que abre.
  if (!abierto && !lista.length) {
    return (
      <div style={{ marginBottom: 10 }}>
        <button className="btn" type="button" onClick={() => setAbierto(true)}
          style={{ background: "none", border: "none", padding: 0, cursor: "pointer",
            display: "flex", alignItems: "center", gap: 5, fontSize: 11.5, fontWeight: 600,
            fontFamily: "var(--font)", color: "var(--accent)" }}>
          <Icon name="plus-lg" size={11} color="var(--accent)"/>Añadir subtareas
        </button>
      </div>
    );
  }

  const d = descuadre(lista, minutosEntrada);

  // Sin la clase cw-field a proposito: su regla `.cw-field input {width:100%}`
  // alcanza a TODOS los inputs de dentro, y una casilla al 100% del ancho se
  // come la fila y deja el texto y los minutos aplastados a cero. La etiqueta
  // lleva el mismo estilo escrito a mano.
  return (
    <div style={{ marginBottom: 10, display: "flex", flexDirection: "column", gap: 3 }}>
      <Campo><label style={{ fontSize: 9, fontWeight: 700, textTransform: "uppercase",
        letterSpacing: ".07em", color: "var(--text3)" }}>
        Subtareas{lista.length > 0 && ` · ${lista.filter(s => s.hecha).length}/${lista.length}`}
      </label>

      <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
        {lista.map((s, i) => (
          <div key={s.id}
            style={{ display: "flex", alignItems: "center", gap: 6, padding: "4px 7px",
              borderRadius: 8, background: "var(--surface2)", border: "1px solid var(--border)" }}>

            {/* Reordenar con flechas y no arrastrando: la tarjeta de la entrada
                ya tiene su propio arrastre y anidar dos es un nido de bugs. */}
            <span style={{ display: "flex", flexShrink: 0 }}>
              <button aria-label="Subir" className="btn" type="button" title="Subir" disabled={i === 0}
                onClick={() => emitir(mover(lista, s.id, -1))}
                style={{ background: "none", border: "none", padding: 0, lineHeight: 0, minWidth: 24, minHeight: 24,
                  display: "inline-flex", alignItems: "center", justifyContent: "center",
                  cursor: i === 0 ? "default" : "pointer", opacity: i === 0 ? .2 : .8 }}>
                <Icon name="chevron-up" size={9} color="var(--text2)"/>
              </button>
              <button aria-label="Bajar" className="btn" type="button" title="Bajar" disabled={i === lista.length - 1}
                onClick={() => emitir(mover(lista, s.id, 1))}
                style={{ background: "none", border: "none", padding: 0, lineHeight: 0, minWidth: 24, minHeight: 24,
                  display: "inline-flex", alignItems: "center", justifyContent: "center",
                  cursor: i === lista.length - 1 ? "default" : "pointer",
                  opacity: i === lista.length - 1 ? .2 : .8 }}>
                <Icon name="chevron-down" size={9} color="var(--text2)"/>
              </button>
            </span>

            {/* El ancho va explícito en todos: sin él, una regla de CSS del
                entorno puede estirar la casilla a lo ancho de la fila. */}
            <input type="checkbox" checked={s.hecha} title="Hecha"
              onChange={() => emitir(toggle(lista, s.id))}
              style={{ width: 14, height: 14, flexShrink: 0, cursor: "pointer", margin: 0 }}/>

            <input value={s.texto}
              onChange={e => emitir(setTexto(lista, s.id, e.target.value), true)}
              placeholder="Qué hay que hacer…"
              style={{ flex: "1 1 auto", width: "auto", minWidth: 0,
                padding: "5px 8px", borderRadius: 7,
                border: "1px solid var(--border)", background: "var(--surface)",
                fontSize: 12, fontFamily: "var(--font)",
                color: s.hecha ? "var(--text3)" : "var(--text)",
                textDecoration: s.hecha ? "line-through" : "none" }}/>

            <MinutosInput value={s.minutos}
              onChange={m => emitir(setMinutos(lista, s.id, m))}/>

            <button aria-label="Quitar" className="btn" type="button" title="Quitar"
              onClick={() => emitir(quitar(lista, s.id))}
              style={{ background: "none", border: "none", padding: 2, cursor: "pointer",
                display: "flex", flexShrink: 0, opacity: .4 }}>
              <Icon name="trash3" size={11} color="var(--red-text)"/>
            </button>
          </div>
        ))}
      </div></Campo>

      <div style={{ display: "flex", gap: 6, marginTop: 6 }}>
        <input aria-label="Nueva subtarea" value={nueva}
          onChange={e => setNueva(e.target.value)}
          onKeyDown={e => { if (e.key === "Enter") { e.preventDefault(); anadir(); } }}
          placeholder="Añadir subtarea y pulsar Enter…"
          style={{ flex: "1 1 auto", width: "auto", minWidth: 0, padding: "6px 9px", borderRadius: 8,
            border: "1px solid var(--border)", fontSize: 12, fontFamily: "var(--font)",
            background: "var(--surface2)", color: "var(--text)" }}/>
        <button className="btn" type="button" onClick={anadir} disabled={!nueva.trim()}
          style={{ padding: "6px 11px", borderRadius: 8, border: "1px solid var(--border)",
            background: "var(--surface)", color: "var(--accent)", fontSize: 11.5,
            fontWeight: 700, fontFamily: "var(--font)",
            cursor: nueva.trim() ? "pointer" : "default", opacity: nueva.trim() ? 1 : .45 }}>
          Añadir
        </button>
      </div>

      {/* El tiempo de la entrada NO se toca solo. Lo único que lo cambia es este
          botón, y hay que pulsarlo. */}
      {d.estado === "cuadra" && (
        <div style={{ display: "flex", alignItems: "center", gap: 6, marginTop: 7,
          fontSize: 11, color: "#047857" }}>
          <Icon name="check2-square" size={11} color="#047857"/>
          Las subtareas cuadran con el tiempo de la entrada.
          {d.incompleto && " Quedan subtareas sin estimar."}
        </div>
      )}

      {(d.estado === "faltan" || d.estado === "sobran") && (
        <div style={{ display: "flex", alignItems: "center", gap: 7, flexWrap: "wrap",
          marginTop: 7, padding: "6px 9px", borderRadius: 8, background: "var(--warn-bg)",
          border: "1px solid var(--warn-line)", fontSize: 11, color: "var(--warn-text)" }}>
          <Icon name="exclamation-triangle-fill" size={11} color="#a16207"/>
          <span>
            Las subtareas suman <b>{fmtDur(d.suma)}</b> y la entrada tiene <b>{fmtDur(d.total)}</b>
            {d.estado === "faltan"
              ? ` · quedan ${fmtDur(-d.diff)} sin repartir`
              : ` · se pasan ${fmtDur(d.diff)}`}
            {d.incompleto && ", y hay subtareas sin estimar"}
          </span>
          <button className="btn" type="button" onClick={() => onAjustarTiempo?.(d.suma)}
            style={{ marginLeft: "auto", padding: "3px 9px", borderRadius: 7,
              border: "1px solid #fbbf24", background: "var(--warn-bg)", color: "var(--warn-text)",
              fontSize: 10.5, fontWeight: 700, fontFamily: "var(--font)", cursor: "pointer" }}>
            Ajustar a {fmtDur(d.suma)}
          </button>
        </div>
      )}
    </div>
  );
}
